import React, { useState, useEffect, useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { Spinner } from "reactstrap"
import { setBreadcrumbItems } from "../../../store/actions"
import { getAnalisisVentas } from "./api"
import HeaderFiltros from "./HeaderFiltros"
import ExportarBoton from "./ExportarBoton"
import ResumenTab from "./tabs/ResumenTab"
import SucursalesTab from "./tabs/SucursalesTab"
import MetodosPagoTab from "./tabs/MetodosPagoTab"
import ProductosTab from "./tabs/ProductosTab"
import CargosTab from "./tabs/CargosTab"
import ComparativoTab from "./tabs/ComparativoTab"

const breadcrumbItems = [
  { title: "LS-DA", link: "#" },
  { title: "Comprobantes", link: "#" },
  { title: "Análisis de Ventas", link: "#" },
]

const TABS = [
  { id: "resumen", label: "Resumen", icon: "mdi-view-dashboard-outline" },
  { id: "sucursales", label: "Sucursales", icon: "mdi-store" },
  { id: "metodos", label: "Métodos de pago", icon: "mdi-credit-card-outline" },
  { id: "productos", label: "Productos", icon: "mdi-package-variant-closed" },
  { id: "cargos", label: "Cargos", icon: "mdi-tag-text-outline" },
  { id: "comparativo", label: "Comparativo", icon: "mdi-chart-timeline-variant" },
]

const ahora = new Date()

const AnalisisVentas = ({ empresaSeleccionada, user, setBreadcrumbItems: setBreadcrumb }) => {
  // Filtros
  const [modo, setModo] = useState("mes")
  const [year, setYear] = useState(ahora.getFullYear())
  const [month, setMonth] = useState(ahora.getMonth() + 1)
  const [fechaInicio, setFechaInicio] = useState("")
  const [fechaFin, setFechaFin] = useState("")
  const [campoFecha, setCampoFecha] = useState("fecha")

  // Estado data
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [tabActivo, setTabActivo] = useState("resumen")

  // Filtros aplicados (se actualizan al pulsar Consultar)
  const [filtrosAplicados, setFiltrosAplicados] = useState(null)

  const nitemisor = empresaSeleccionada?.nit || empresaSeleccionada?.identificacion || ""
  const empresaNombre = empresaSeleccionada?.nombre || ""

  useEffect(() => {
    document.title = "Análisis de Ventas | LS-DA"
    const greeting = user
      ? `Bienvenido, ${user.nombre || ""} ${user.apellido || ""}`.trim()
      : "Análisis de Ventas"
    setBreadcrumb(greeting, breadcrumbItems)
  }, [user, setBreadcrumb])

  // Reset al cambiar empresa
  useEffect(() => {
    setData(null)
    setError("")
    setFiltrosAplicados(null)
    setTabActivo("resumen")
  }, [nitemisor])

  const puedeConsultar = useMemo(() => {
    if (!nitemisor) return false
    if (modo === "mes") return Boolean(year && month)
    return Boolean(fechaInicio && fechaFin && fechaInicio <= fechaFin)
  }, [nitemisor, modo, year, month, fechaInicio, fechaFin])

  const cargar = useCallback(async (opts) => {
    setLoading(true)
    setError("")
    try {
      const response = await getAnalisisVentas(opts)
      const payload = response.data
      if (payload?.success === false) {
        throw new Error(payload?.message || "El servidor reportó un error.")
      }
      setData(payload?.data || payload)
    } catch (err) {
      setError(err.response?.data?.message || err.message || "No se pudo cargar el análisis.")
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleConsultar = () => {
    const opts = {
      nitemisor,
      modo,
      year,
      month,
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin,
      campo_fecha: campoFecha,
    }
    setFiltrosAplicados(opts)
    cargar(opts)
  }

  // Sin empresa
  if (!nitemisor) {
    return (
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: 60,
          textAlign: "center",
          border: "1px dashed #cbd5e1",
        }}
      >
        <i className="mdi mdi-domain-off" style={{ fontSize: 56, color: "#cbd5e1" }} />
        <h5 className="mt-3" style={{ color: "#0f172a" }}>Selecciona una empresa</h5>
        <p style={{ color: "#64748b", maxWidth: 420, margin: "8px auto 0" }}>
          Usa el selector <strong>“Seleccionar empresa”</strong> en la parte superior derecha
          para iniciar el análisis.
        </p>
      </div>
    )
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div
        style={{
          background: "#fff",
          borderRadius: 14,
          padding: "20px 24px",
          border: "1px solid #e2e8f0",
        }}
      >
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
          <div>
            <div style={{ fontSize: 12, color: "#64748b", textTransform: "uppercase", fontWeight: 600, letterSpacing: 0.4 }}>
              Comprobantes
            </div>
            <h3 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: "#0f172a" }}>
              Análisis de Ventas
            </h3>
            <div style={{ fontSize: 13, color: "#64748b", marginTop: 4 }}>
              <i className="mdi mdi-domain me-1 text-primary" />
              <strong>{empresaNombre}</strong> · NIT {nitemisor}
            </div>
          </div>
          {data && filtrosAplicados && (
            <ExportarBoton exportOpts={filtrosAplicados} size="sm" />
          )}
        </div>

        <HeaderFiltros
          modo={modo} setModo={setModo}
          year={year} setYear={setYear}
          month={month} setMonth={setMonth}
          fechaInicio={fechaInicio} setFechaInicio={setFechaInicio}
          fechaFin={fechaFin} setFechaFin={setFechaFin}
          campoFecha={campoFecha} setCampoFecha={setCampoFecha}
          loading={loading}
          onConsultar={handleConsultar}
          puedeConsultar={puedeConsultar}
        />
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            padding: "14px 18px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            borderRadius: 10,
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <i className="mdi mdi-alert-circle" style={{ fontSize: 20 }} />
          <span>{error}</span>
          <button type="button" className="btn btn-sm btn-outline-danger ms-auto" onClick={() => filtrosAplicados && cargar(filtrosAplicados)}>
            Reintentar
          </button>
        </div>
      )}

      {/* Loading inicial */}
      {!data && loading && (
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: 80,
            textAlign: "center",
            color: "#64748b",
            border: "1px solid #e2e8f0",
          }}
        >
          <Spinner color="primary" />
          <div className="mt-2">Cargando análisis…</div>
        </div>
      )}

      {/* Empty state inicial (sin consulta) */}
      {!data && !loading && !error && (
        <div
          style={{
            background: "#fff",
            borderRadius: 14,
            padding: "60px 40px",
            textAlign: "center",
            border: "1px dashed #cbd5e1",
          }}
        >
          <i className="mdi mdi-chart-line" style={{ fontSize: 56, color: "#cbd5e1" }} />
          <h5 className="mt-3" style={{ color: "#0f172a", fontWeight: 600 }}>
            Configura los filtros y consulta
          </h5>
          <p style={{ color: "#64748b", maxWidth: 460, margin: "8px auto 0", fontSize: 14 }}>
            Selecciona el modo (mes o rango), el campo de fecha y pulsa <strong>Consultar</strong> para
            ver el análisis de ventas de <strong>{empresaNombre}</strong>.
          </p>
        </div>
      )}

      {/* Tabs + contenido */}
      {data && (
        <>
          {/* Tabs */}
          <div
            style={{
              background: "#fff",
              borderRadius: "14px 14px 0 0",
              border: "1px solid #e2e8f0",
              borderBottom: "none",
              padding: "0 12px",
              display: "flex",
              gap: 4,
              overflowX: "auto",
            }}
          >
            {TABS.map(tab => {
              const activo = tabActivo === tab.id
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setTabActivo(tab.id)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: "14px 18px",
                    fontSize: 13,
                    fontWeight: 600,
                    color: activo ? "#0f172a" : "#64748b",
                    borderBottom: activo ? "3px solid #3b82f6" : "3px solid transparent",
                    cursor: "pointer",
                    transition: "color 0.15s",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    whiteSpace: "nowrap",
                  }}
                >
                  <i className={`mdi ${tab.icon}`} />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {/* Tab content */}
          <div
            style={{
              background: tabActivo === "resumen" ? "transparent" : "transparent",
              borderRadius: tabActivo === "sucursales" || tabActivo === "metodos" || tabActivo === "productos" || tabActivo === "cargos" ? 0 : 0,
            }}
          >
            {tabActivo === "resumen" && <ResumenTab data={data} />}
            {tabActivo === "sucursales" && <SucursalesTab data={data} exportOpts={filtrosAplicados} />}
            {tabActivo === "metodos" && <MetodosPagoTab data={data} />}
            {tabActivo === "productos" && <ProductosTab data={data} />}
            {tabActivo === "cargos" && <CargosTab data={data} />}
            {tabActivo === "comparativo" && <ComparativoTab data={data} />}
          </div>
        </>
      )}
    </div>
  )
}

AnalisisVentas.propTypes = {
  empresaSeleccionada: PropTypes.object,
  user: PropTypes.object,
  setBreadcrumbItems: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({
  empresaSeleccionada: state.Login.empresaSeleccionada,
  user: state.Login.user,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(AnalisisVentas)
